import type { Recurrence, RecurrenceDayOfWeek, RecurrenceIndex } from "./types.ts";

/**
 * Graph recurrence pattern → RFC 5545 RRULE (ARCHITECTURE.md §8: "build ICS
 * client-side; handle recurrence master + exceptions").
 *
 * Pure and dependency-free so it is unit-testable under plain Node, the same
 * pattern every module here follows for logic that doesn't touch Graph/auth.
 *
 * `range.endDate` is a plain calendar date ("2026-01-01"), not a date-time —
 * it is not affected by the `Prefer: outlook.timezone="UTC"` header the
 * export requests for start/end times (§ export.ts). UNTIL is built as the
 * end of that day in UTC, a deliberate approximation: RFC 5545 wants UNTIL in
 * the same value type as DTSTART (UTC date-time, since every DTSTART here is
 * UTC), and Graph does not say what wall-clock time within `endDate` the
 * range is meant to end at.
 */

const DAY_CODES: Record<RecurrenceDayOfWeek, string> = {
    sunday: "SU",
    monday: "MO",
    tuesday: "TU",
    wednesday: "WE",
    thursday: "TH",
    friday: "FR",
    saturday: "SA"
};

const INDEX_CODES: Record<RecurrenceIndex, string> = {
    first: "1",
    second: "2",
    third: "3",
    fourth: "4",
    last: "-1"
};

function byDay(days: readonly RecurrenceDayOfWeek[] | undefined): string | null {
    if (!days || days.length === 0) {
        return null;
    }
    return days.map((d) => DAY_CODES[d]).join(",");
}

/** `2026-01-01` → `20260101T235959Z` — see the module-level note on why end-of-day. */
function untilFromDate(dateOnly: string): string | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOnly);
    if (!match) {
        return null;
    }
    const [, y, m, d] = match;
    return `${y}${m}${d}T235959Z`;
}

function rangeSuffix(range: Recurrence["range"]): string {
    switch (range.type) {
        case "numbered":
            return range.numberOfOccurrences !== undefined ? `;COUNT=${range.numberOfOccurrences}` : "";
        case "endDate": {
            const until = range.endDate ? untilFromDate(range.endDate) : null;
            return until ? `;UNTIL=${until}` : "";
        }
        case "noEnd":
        default:
            return "";
    }
}

/**
 * Builds an RRULE value (without the `RRULE:` property name) from a Graph
 * recurrence object, or null for a pattern type this mapper does not
 * recognise — a series with no RRULE this function can produce is exported
 * as a standalone event by the caller rather than dropped, so nothing is
 * silently lost, only the "it recurs" information.
 */
export function buildRRule(recurrence: Recurrence): string | null {
    const { pattern, range } = recurrence;
    const interval = pattern.interval > 0 ? pattern.interval : 1;
    const suffix = rangeSuffix(range);

    switch (pattern.type) {
        case "daily":
            return `FREQ=DAILY;INTERVAL=${interval}${suffix}`;

        case "weekly": {
            const days = byDay(pattern.daysOfWeek);
            return `FREQ=WEEKLY;INTERVAL=${interval}${days ? `;BYDAY=${days}` : ""}${suffix}`;
        }

        case "absoluteMonthly":
            return pattern.dayOfMonth !== undefined
                ? `FREQ=MONTHLY;INTERVAL=${interval};BYMONTHDAY=${pattern.dayOfMonth}${suffix}`
                : `FREQ=MONTHLY;INTERVAL=${interval}${suffix}`;

        case "relativeMonthly": {
            const days = byDay(pattern.daysOfWeek);
            const index = pattern.index ? INDEX_CODES[pattern.index] : null;
            if (!days || !index) {
                return `FREQ=MONTHLY;INTERVAL=${interval}${suffix}`;
            }
            // Graph allows multiple daysOfWeek with one shared index (e.g. "the
            // last weekday of the month") — RRULE expects the index on each
            // BYDAY term individually.
            const byDayWithIndex = days
                .split(",")
                .map((code) => `${index}${code}`)
                .join(",");
            return `FREQ=MONTHLY;INTERVAL=${interval};BYDAY=${byDayWithIndex}${suffix}`;
        }

        case "absoluteYearly": {
            const parts = [`FREQ=YEARLY`, `INTERVAL=${interval}`];
            if (pattern.month !== undefined) {
                parts.push(`BYMONTH=${pattern.month}`);
            }
            if (pattern.dayOfMonth !== undefined) {
                parts.push(`BYMONTHDAY=${pattern.dayOfMonth}`);
            }
            return parts.join(";") + suffix;
        }

        case "relativeYearly": {
            const days = byDay(pattern.daysOfWeek);
            const index = pattern.index ? INDEX_CODES[pattern.index] : null;
            const parts = [`FREQ=YEARLY`, `INTERVAL=${interval}`];
            if (pattern.month !== undefined) {
                parts.push(`BYMONTH=${pattern.month}`);
            }
            if (days && index) {
                parts.push(`BYDAY=${days.split(",").map((code) => `${index}${code}`).join(",")}`);
            }
            return parts.join(";") + suffix;
        }

        default:
            return null;
    }
}
